"use client";

import React, { FC, useState, useEffect, ComponentType } from "react";
import { BottomNavigationMobile } from "../../interfaces/IHeader.interface";
import { getChildByNodeName } from "@repo/shared-utils/common-utils";
import Conditional from "@repo/shared-ui/Conditional";
import dynamic from "next/dynamic";
import { SignOutProps } from "./SignOut";
import { selectIsAuthSuccessOrPerpetual, useAppSelector } from "@repo/global-store";
import {
  LOGGED_IN_MY_ACC_URL,
  NON_LOGGED_IN_MY_ACC_URL,
  MENU_EN,
  MENU_HI,
} from "../../constants/HeaderConstants";
import BFLLink from "@repo/shared-ui/BFLLink";
import FollowUsList from "./FollowUsList";
import FirstName from "./FirstName";
import {
  handleHeaderMenuClickDL,
  handleHeaderMenuCloseClickDL,
  handleHeaderMainMenuClickDL,
  handleHeaderSidebarProfileClickDL,
  handleHeaderSidebarMenuButtonClickDL,
} from "./HeaderAnalyticsFunction";

const SignOutLink: ComponentType<SignOutProps> = dynamic(() => import("./SignOut.tsx") as any, {
  ssr: false,
});
interface MenuToggleProps {
  bottomnavigationmobile?: BottomNavigationMobile[];
  language?: string;
}

const MenuToggle: FC<MenuToggleProps> = ({ bottomnavigationmobile, language = "en" }) => {
  interface HamburgerMenuChild {
    nodeName: string;
    [key: string]: any;
  }

  interface BottomNavigationMobileWithChildren extends BottomNavigationMobile {
    children?: HamburgerMenuChild[];
  }

  const hamburgerMenu = (
    bottomnavigationmobile as BottomNavigationMobileWithChildren | undefined
  )?.children?.find?.((item: HamburgerMenuChild) => item.nodeName === "hamburgermenu");

  // Helper function to safely extract menu items
  const extractMenuItems = (node: any): any[] => {
    if (!node) return [];

    // If it's an array, return it directly
    if (Array.isArray(node)) return node;

    // If it has a children property that's an array, return that
    if (node.children && Array.isArray(node.children)) return node.children;

    // Otherwise return an empty array
    return [];
  };

  // Get the menu lists and ensure they have a default value if missing
  const hamburgerMenuListNode = getChildByNodeName(hamburgerMenu, "hamburgerMenuList");
  const mainMenuListNode = getChildByNodeName(hamburgerMenu, "mainMenuList");
  const followUsListnode = getChildByNodeName(hamburgerMenu, "followUsMenuList");

  // Get children arrays from the menu nodes using the helper function
  const hamburgerMenuList = extractMenuItems(hamburgerMenuListNode);
  const mainMenuList = extractMenuItems(mainMenuListNode);
  const followUsList = extractMenuItems(followUsListnode);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showSubMenu, setShowSubMenu] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState("");
  const [selectedSubMenu, setSelectedSubMenu] = useState<any[]>([]);
  const [mounted, setMounted] = useState(false);
  const isAuthSuccessOrPerpetualRaw = useAppSelector(selectIsAuthSuccessOrPerpetual);
  // Defer auth state to client-only to prevent SSR/client hydration mismatch
  const isAuthSuccessOrPerpetual = mounted && isAuthSuccessOrPerpetualRaw;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isSidebarOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isSidebarOpen]);

  // Get menu text based on language
  const menuDisplayText = language === "hi" ? MENU_HI : MENU_EN;

  const toggleSidebar = () => {
    // Track menu button click analytics
    handleHeaderMenuClickDL(menuDisplayText);

    const element = document.querySelector(".menu-header-bar");
    if (element) {
      element.classList.remove("z-5");
      element.classList.add("z-10");
    }
    setIsSidebarOpen((prev) => !prev);
    setShowSubMenu(false);
  };

  const closeSidebar = () => {
    // Track menu close button click analytics
    handleHeaderMenuCloseClickDL("Close Menu");

    const element = document.querySelector(".menu-header-bar");
    if (element) {
      element.classList.remove("z-10");
      element.classList.add("z-5");
    }
    setIsSidebarOpen(false);
    setShowSubMenu(false);
  };

  // Helper function to find submenu items
  const findSubmenuItems = (item: any, detailsNodeName: string): any[] => {
    // Check for direct property access first
    const directPropertyName =
      detailsNodeName === "secondaryMenuDetails" ? "secondaryMenuDetails" : "mainMenuDetails";

    if (item?.[directPropertyName]) {
      return item[directPropertyName];
    }

    // Look in children array if it exists
    if (item?.children && Array.isArray(item.children)) {
      const detailsNode = item.children.find((child: any) => child?.nodeName === detailsNodeName);

      if (detailsNode?.children && Array.isArray(detailsNode.children)) {
        return detailsNode.children;
      }
    }
    return [];
  };

  const handleMainMenuClick = (menuTitle: string, source: "hamburger" | "main") => {
    // Track main menu item click analytics
    handleHeaderMainMenuClickDL(menuTitle, source);

    let matchedItem;
    let submenuItems: any[] = [];

    if (source === "hamburger") {
      matchedItem = hamburgerMenuList.find((item: any) => item?.hamburgerMenuTitle === menuTitle);
      submenuItems = findSubmenuItems(matchedItem, "secondaryMenuDetails");
    } else {
      matchedItem = mainMenuList.find((item: any) => item?.mainMenuLabel === menuTitle);
      submenuItems = findSubmenuItems(matchedItem, "mainMenuDetails");
    }

    setSelectedSubMenu(submenuItems);
    setSelectedTitle(menuTitle);
    setShowSubMenu(true);
  };

  return (
    <>
      <button
        onClick={toggleSidebar}
        className="flex cursor-pointer border-none bg-transparent px-[6px] py-[10px] text-sm leading-[20px] hover:bg-blue-900"
        aria-label="Open menu"
      >
        <i className="icomoon mr-1 text-[18px] before:content-['\e958']" /> {menuDisplayText}
      </button>

      {isSidebarOpen && (
        <button
          onClick={closeSidebar}
          data-testid="menu-overlay"
          className="fixed inset-0 z-20 overflow-hidden bg-[rgba(0,0,0,0.5)]"
          aria-label="Close menu overlay"
        />
      )}

      <div
        className={`fixed top-0 left-0 z-50 h-[100vh] w-[360px] transform bg-white text-white shadow-lg transition-transform duration-300 ease-in-out ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="bg-bfl-blue flex items-center justify-between border-b border-white/20 px-6 py-3">
          <BFLLink
            className="flex cursor-pointer items-center space-x-2"
            href={isAuthSuccessOrPerpetual ? LOGGED_IN_MY_ACC_URL : NON_LOGGED_IN_MY_ACC_URL}
            onClick={() => {
              // Track sidebar profile/sign in link click analytics
              const url = isAuthSuccessOrPerpetual
                ? LOGGED_IN_MY_ACC_URL
                : NON_LOGGED_IN_MY_ACC_URL;
              const text = isAuthSuccessOrPerpetual ? "My Account" : "Sign In";
              handleHeaderSidebarProfileClickDL(url, text);
            }}
          >
            <i className="icomoon mr-3 text-[20px] before:content-['\e954']"></i>
            <span className="text-base">
              {" "}
              <Conditional if={!isAuthSuccessOrPerpetual}>
                <>{"Sign In"}</>
              </Conditional>
              <Conditional if={isAuthSuccessOrPerpetual}>
                <FirstName />
              </Conditional>
            </span>
          </BFLLink>
          <button onClick={closeSidebar} className="cursor-pointer text-xl font-[500] text-white">
            ✕
          </button>
        </div>

        <div className="no-scrollbar h-full overflow-y-auto pb-[80px]">
          {!showSubMenu ? (
            <>
              <div className="space-y-2 text-gray-900">
                {hamburgerMenuList.length > 0 ? (
                  hamburgerMenuList.map((item: any) => (
                    <button
                      key={item?.key || item?.nodeName || item?.hamburgerMenuTitle}
                      className="mb-0 flex w-full cursor-pointer items-center justify-between px-6 py-3 text-left text-sm hover:bg-gray-200"
                      onClick={() => {
                        // Track analytics for hamburger menu button click
                        handleHeaderSidebarMenuButtonClickDL(item?.hamburgerMenuTitle || "");

                        // Execute original menu handling logic
                        handleMainMenuClick(item?.hamburgerMenuTitle, "hamburger");
                      }}
                      aria-label={`Open ${item?.hamburgerMenuTitle} submenu`}
                    >
                      {item?.hamburgerMenuTitle}
                      <i
                        className="icomoon ml-1 text-xs before:content-['\e911']"
                        aria-hidden="true"
                      ></i>
                    </button>
                  ))
                ) : (
                  <div className="px-6 py-3 text-gray-400">No menu items available</div>
                )}
              </div>

              <div className="mt-2 space-y-2 border-t border-b border-gray-300 pt-2 text-gray-900">
                {mainMenuList.map((item: any) => (
                  <button
                    key={item?.key || item?.nodeName || item?.mainMenuLabel}
                    className="mb-0 flex w-full items-center justify-between px-6 py-3 text-left text-sm hover:bg-gray-200"
                    onClick={() => {
                      // Track analytics for menu button click
                      handleHeaderSidebarMenuButtonClickDL(item?.mainMenuLabel || "");

                      // Execute original menu handling logic
                      return item?.mainMenuDetails?.length > 0 ||
                        (item?.children && item?.children.length > 0)
                        ? handleMainMenuClick(item?.mainMenuLabel, "main")
                        : null;
                    }}
                    aria-label={`Open submenu for ${item?.mainMenuLabel}`}
                  >
                    {item?.mainMenuLabel}
                    <span
                      className="icomoon text-xs before:content-['\e911']"
                      aria-hidden="true"
                    ></span>
                  </button>
                ))}
              </div>
              <FollowUsList followUsList={followUsList} />
            </>
          ) : (
            <div>
              <button
                onClick={() => setShowSubMenu(false)}
                className="flex w-full cursor-pointer items-center bg-gray-200 px-6 py-3 text-left text-base font-[500] text-gray-900 uppercase"
              >
                {/* Back arrow */}
                &larr; Main Menu
              </button>
              <h3 className="px-6 py-3 text-base font-[500] text-gray-900">{selectedTitle}</h3>
              <div className="space-y-2 text-gray-900">
                {selectedSubMenu.map((item: any) => (
                  <React.Fragment
                    key={
                      item?.key ||
                      item?.nodeName ||
                      item?.secondaryMenuTitle ||
                      item?.mainMenuListTitle
                    }
                  >
                    <Conditional if={item.key !== "logout-settings"}>
                      <div className="mb-0 flex cursor-pointer items-center space-x-3 px-6 py-3 text-sm hover:bg-gray-200">
                        <a
                          href={item.hamburgerCtaLink || item.mainMenuCtaLink}
                          target={
                            item.ctaOpenInNewTab === "true" || item.mainMenuOpenInNewTab === "true"
                              ? "_blank"
                              : "_self"
                          }
                          rel="noopener noreferrer"
                          className="text-gray-900"
                        >
                          {item.secondaryMenuTitle || item.mainMenuListTitle}
                        </a>
                      </div>
                    </Conditional>

                    <Conditional if={item.key == "logout-settings"}>
                      <SignOutLink signoutLabel={item.mainMenuListTitle} />
                    </Conditional>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MenuToggle;
